import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/operators';
import { Hotkey } from '../_models/hotkey';

@Injectable({
  providedIn: 'root',
})
export class HotkeysService {
  private hotkeys: Hotkey[] = [];
  public hotkeys$ = new BehaviorSubject<Hotkey[]>([]);
  public hotkeysObservable = this.hotkeys$.asObservable();
  private hotkey!: Hotkey;

  constructor(private api: ApiService, private http: HttpClient) {
    this.hotkeys = [];
  }

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  addHotkey(newHotkey: any): Observable<Hotkey> {
    return this.api.postAPI<Hotkey>('hotkeys', newHotkey);
  }

  getHotkeyOptionsList(): Hotkey[] {
    return this.hotkeys;
  }

  getHotkeys(): Observable<Hotkey[]> {
    const value = this.api
      .getAPI<Hotkey[]>('hotkeys')
      .pipe(map((response) => response.body));
    const formattedValue = value.pipe(
      map((hotkeys) => this.transformData(hotkeys))
    );
    return formattedValue;
  }

  fetchHotkeys(): void {
    this.api.getAPI<Hotkey[]>('hotkeys').subscribe((hotkeys: Hotkey[]) => {
      this.hotkeys = hotkeys;
      this.hotkeys$.next(this.hotkeys);
    });
  }

  updateHotkey(id: number, hotkey: any): Observable<Hotkey> {
    return this.api.updateAPI<Hotkey>('hotkeys', id, hotkey);
  }

  updateTag(id: number, body: any): Observable<Hotkey> {
    return this.api.updateAPI<Hotkey>('hotkeys/update-tag', id, body);
  }

  filterHotkeys(query: string): Observable<Hotkey[]> {
    const q = query.toLowerCase();
    return this.getHotkeys().pipe(
      map((hotkeys) =>
        hotkeys
          .filter((hotkey: Hotkey) =>
            (hotkey.hotkey_main || '').toLowerCase().includes(q) ||
            (hotkey.hotkey_tag || '').toLowerCase().includes(q) ||
            (hotkey.tag_description || '').toLowerCase().includes(q) ||
            (hotkey.main_description || '').toLowerCase().includes(q)
          )
          .sort((a, b) =>
            (a.hotkey_main || '').localeCompare(b.hotkey_main || '') ||
            (a.hotkey_tag || '').localeCompare(b.hotkey_tag || '')
          )
      )
    );
  }

  deleteHotkey(id: number): Observable<Hotkey> {
    return this.api.deleteAPI<Hotkey>('hotkeys', id);
  }

  /** Raw shortcut rows (one per component+detail pair). */
  private rawShortcuts(): Observable<Hotkey[]> {
    return this.api
      .getAPI<Hotkey[]>('hotkeys')
      .pipe(map((res) => res.body || []));
  }

  /** Distinct component names (the "Description" suggestions), filtered by a
   *  query, sorted. */
  getComponents(query = ''): Observable<string[]> {
    const q = query.trim().toLowerCase();
    return this.rawShortcuts().pipe(
      map((rows) => {
        const set = new Map<string, string>();
        rows.forEach((r) => {
          const c = (r.main_description || '').trim();
          if (c && c.toLowerCase().includes(q)) set.set(c.toLowerCase(), c);
        });
        return Array.from(set.values()).sort((a, b) => a.localeCompare(b));
      })
    );
  }

  /** Detail lines (the "Remarks" suggestions) used for a given component,
   *  filtered by a query, sorted. */
  getDetailsForComponent(component: string, query = ''): Observable<string[]> {
    const comp = (component || '').trim().toLowerCase();
    const q = query.trim().toLowerCase();
    return this.rawShortcuts().pipe(
      map((rows) => {
        const set = new Map<string, string>();
        rows.forEach((r) => {
          if ((r.main_description || '').trim().toLowerCase() !== comp) return;
          const d = (r.tag_description || '').trim();
          if (d && d.toLowerCase().includes(q)) set.set(d.toLowerCase(), d);
        });
        return Array.from(set.values()).sort((a, b) => a.localeCompare(b));
      })
    );
  }

  /** Ask the AI module to draft a Remarks detail for a component. Degrades
   *  server-side to the component's known details when no AI provider is
   *  configured (source="existing"), so this is always safe to call. */
  draftRemark(
    component: string,
    context?: string
  ): Observable<{ remark: string; alternatives: string[]; source: string }> {
    return this.api
      .postAPI<{ remark: string; alternatives: string[]; source: string }>(
        'hotkeys/draft-remark',
        { component, context: context ?? null }
      )
      .pipe(map((res) => res.body));
  }

  /** Create a shortcut (component + optional detail) if that exact pair does
   *  not already exist. Best-effort; used to persist free-typed entries. */
  ensureShortcut(component: string, detail = ''): void {
    const comp = (component || '').trim();
    if (!comp) return;
    const det = (detail || '').trim();
    this.rawShortcuts().subscribe({
      next: (rows) => {
        const exists = rows.some(
          (r) =>
            (r.main_description || '').trim().toLowerCase() ===
              comp.toLowerCase() &&
            (r.tag_description || '').trim().toLowerCase() === det.toLowerCase()
        );
        if (exists) return;
        this.addHotkey({
          hotkey_main: '',
          hotkey_tag: '',
          main_description: comp.slice(0, 30),
          tag_description: det.slice(0, 100),
        }).subscribe({ next: () => {}, error: () => {} });
      },
      error: () => {},
    });
  }

  updateHotkeys(hotkeys: Hotkey[]): void {
    this.hotkeys$.next(hotkeys);
  }

  transformData(hotkeys: any): any[] {
    const mergedHotkeys: any[] = [];

    hotkeys.forEach((hotkey: any) => {
      const existingHotkey = mergedHotkeys.find(
        (i) => i.hotkey_main === hotkey.hotkey_main
      );

      if (existingHotkey) {
        // Add a new description to the existing row
        existingHotkey.descriptions.push({
          id: hotkey.id,
          hotkey_tag: hotkey.hotkey_tag,
          hotkey_main: hotkey.hotkey_main,
          main_description: hotkey.main_description,
          tag_description: hotkey.tag_description,
        });
      } else {
        // Add a new entry with a new array of descriptions
        mergedHotkeys.push({
          ...hotkey,
          descriptions: [
            {
              id: hotkey.id,
              hotkey_main: hotkey.hotkey_main,
              main_description: hotkey.main_description,
              tag_description: hotkey.tag_description,
              hotkey_tag: hotkey.hotkey_tag,
            },
          ],
        });
      }
    });
    // // console.log(mergedHotkeys);

    return mergedHotkeys;
  }
}
